import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { MDBIcon } from "mdb-react-ui-kit";
import Lang from "../../../service/lang";
import { CustomSelect } from "../../../components/CustomSelect";
import { Alert } from "../../../components/Alert";

export interface UserEditProps {
    userId: number;
    companyId: number;
    canEditPermissions: boolean;
    canDeleteUser: boolean;
    self: boolean;
    onEdit: () => void;
    onUserUpdate: () => void;
    onClose: () => void;
}
export const UserEdit = ({userId, companyId, canEditPermissions, canDeleteUser, self, onEdit, onUserUpdate, onClose}: UserEditProps) => {
    const [user, setUser] = useState<any|null>(null);
    const [userPermissionsId, setUserPermissionsId] = useState<number|null>(-1);
    const [name, setName] = useState('');
    const [changed, setChanged] = useState(false);
    const [loading, setLoading] = useState(false);
    const [permissions, setPermissions] = useState<any[]>([]);
    const [deleteUser, setDeleteUser] = useState(false);
    const [error, setError] = useState('');
    const { t } = useTranslation();

    const fetch = useCallback(async () => {
        setChanged(false);
        setUserPermissionsId(-1);
        setError('');
        try {
            const rights = (await API.GET('/api/rights')).rights;
            setPermissions(rights);
            const u = await API.GET('/api/users/u/'+userId);
            setUserPermissionsId(u.rights?.id||-1);
            setUser(u);
            setName(u.Name);
        }catch(err: any){
            setError(err.message);
        }
    }, [userId]);

    useEffect(() => {
        fetch();
    }, [userId]);

    const update = useCallback(async () => {
        setLoading(true);
        try {
            await API.PATCH('/api/users/u/'+userId, {
                rightsId: userPermissionsId
            });
            onEdit();
            fetch();
        }catch(err: any){
            setError(err.message);
        }finally{
            setLoading(false);
        };
    }, [userId, user, userPermissionsId]);

    if (!user) return null;

    return (
        <div className={"d-flex flex-column card overflow-hidden "} style={{minWidth: 300}}>
            <DeleteConfirmModal
                open={deleteUser}
                title={t('remove_user')}
                message={t('remove_user_from_company_prompt')}
                onCancel={() => setDeleteUser(false)}
                onConfirm={() => {
                    API.DELETE(`/api/users/u/${userId}`)
                        .then(() => {
                            (self ? onUserUpdate : onEdit)();
                            onClose();
                        })
                        .catch(err => setError(err.message));
                    setDeleteUser(false);
                }}
            />
            <div className="d-flex flex-column card-body overflow-hidden">
                
                <div className="d-flex flex-column overflow-auto pe-1">
                    <div className="d-flex flex-column w-100 justify-content-center text-center">
                        <MDBIcon icon="fas fa-circle-user" size="4x" className="mb-2"/>
                        {user.pending && <h5 className="fst-italic">{t('pending_invitation')}</h5>}
                        {!user.pending && <h5>{name}{self && <span style={{fontStyle: 'italic'}}>&nbsp;({t('me_lower')})</span> }</h5>}
                    </div>

                    {!user.pending && <>
                        <h5 className="ms-2 mb-3">{t('information')}</h5>
                        <ul className="list-group list-group-light p-0">
                            <li className="list-group-item p-1"><MDBIcon icon="fas fa-id-card" fixed className="me-1"/><span className="fw-bold me-1">{t('name')}:</span>{user.Name}</li>
                            <li className="list-group-item p-1"><MDBIcon icon="fas fa-envelope" fixed className="me-1"/><span className="fw-bold me-1">{t('email')}:</span>{user.mail}</li>
                            <li className="list-group-item p-1"><MDBIcon icon="fas fa-mobile-alt" fixed className="me-1"/><span className="fw-bold me-1">{t('phone')}:</span>{user.phone}</li>
                        </ul>
                    </>}

                    {user.pending && <>
                        <span className="w-100 d-flex justify-content-center"><img src="/time.svg" width={256} height={256}/></span>
                        <ul className="list-group list-group-light p-0">
                            <li className="list-group-item p-1"><MDBIcon icon="fas fa-envelope" fixed className="me-1"/><span className="fw-bold me-1">{t('email')}:</span>{user.mail}</li>
                            <li className="list-group-item p-1"><MDBIcon icon="fas fa-clock" fixed className="me-1"/><span className="fw-bold me-1">{t('expire_at')}:</span>
                                {user.invitationExpireAt && <span>{new Date(user.invitationExpireAt).toLocaleString(Lang.instance.getCurrentLocale().code)}</span> }
                                {!user.invitationExpireAt && <>
                                    <span className="text-danger fst-italic">{t('expired')}</span>
                                    <button className="btn btn-link p-1 ms-2" onClick={() => {
                                        API.POST(`/api/companies/${companyId}/invite`, {email: user.mail, rightsId: userPermissionsId})
                                            .then(() => {
                                                onEdit(); onClose();
                                            })
                                            .catch(err => setError(err.message));
                                    }}>{t('resend')}</button>
                                </> }
                            </li>
                        </ul>
                    </>}
                    

                    {(canEditPermissions) && (
                        <div className="d-flex mb-3 flex-row align-items-center w-100">
                            <MDBIcon icon="fas fa-briefcase" fixed className="me-2"/>
                            <CustomSelect w100 title={t('permissions')} items={permissions.map(p => ({ val: p.id, title: p.Name }))} selected={userPermissionsId} onSelect={id => {setUserPermissionsId(id as number); setChanged(true); }} />
                        </div>
                    )}
                    {!canEditPermissions && (
                        <ul className="list-group list-group-light p-0"><li className="list-group-item p-1"><MDBIcon icon="fas fa-briefcase" fixed className="me-1"/><span className="fw-bold me-1">{t('permissions')}:</span>{user.rights?.Name||''}</li></ul>
                    )}
                    <div className="d-flex flex-row w-100">
                        {(canEditPermissions) && (
                            <button className="btn btn-primary me-1 flex-fill" style={{minWidth: 0}} disabled={!changed || loading} onClick={update}>{t('save')}</button>
                        )}
                        {canDeleteUser && (
                            <button className="btn btn-danger flex-fill" style={{minWidth: 0}} disabled={loading} onClick={() => setDeleteUser(true)}>{t('delete')}</button>
                        )}
                    </div>
                    <Alert type="danger" message={error}/>
                </div>
                
                
            </div>
            <button type="button" className="btn-close position-absolute" style={{right: 5, top: 5}} onClick={() => onClose ? onClose() : {}}></button>
        </div>
    ) 
}